import React, { useCallback, useMemo, useState } from 'react';
import { select, t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import { pick } from 'underscore';

import { ServiceTypeFilter } from '@rover/components/src/formFields';
import { ServiceFilterType } from '@rover/components/src/formFields/ServiceTypeFilter/ServiceTypeFilter';
import { LOCATION_DEFAULT_TITLE_2 } from '@rover/constants/src/location/location.constants';
import { Box, Button, Flex } from '@rover/kibble/core';
import {
  emitSearchFormCtaAnalytics,
  SelectDates,
  UpdateLocation,
  UpdateServiceType,
} from '@rover/react-lib/src/pages/landing/utilities/analyticsEvents';
import useDates from '@rover/react-lib/src/pages/landing/utilities/useDates';
import { Location } from '@rover/react-lib/src/pages/search/SearchPage/components';
import type { PaginatedServiceTypeList } from '@rover/rsdk/src/apiClient/latest/model/paginatedServiceTypeList';
import { ServiceTypeSlugEnum } from '@rover/rsdk/src/apiClient/latest/model/serviceTypeSlugEnum';
import { emitAnalyticsEvent } from '@rover/rsdk/src/modules/Analytics';
import FilterLabel from '@rover/shared/js/search/components/FilterLabel';
import { PetType } from '@rover/types/src/Pet';
import type { SearchFilters } from '@rover/types/src/SearchFilters';
import type { ServiceTypeSlug } from '@rover/types/src/ServiceType';

import getSearchUrl from '../../../utils/getSearchUrl';
import { SERVICE_SEARCH_FORM_SUBMIT_TYPES } from '../ServiceLanding/constants';
import {
  DATE_PICKER_END_PLACEHOLDER_FOR_SEO_SLUG,
  DATE_PICKER_START_PLACEHOLDER_FOR_SEO_SLUG,
} from '../utilities/constants';

import DateRangePicker from './DateRangePicker';

export type LandingSearchFormFilters = Partial<SearchFilters> &
  Pick<SearchFilters, 'location' | 'petType' | 'serviceType'> & {
    puppy?: boolean;
  };

type SubmitButtonTextVariant =
  (typeof SERVICE_SEARCH_FORM_SUBMIT_TYPES)[keyof typeof SERVICE_SEARCH_FORM_SUBMIT_TYPES];

export type Props = {
  language: string;
  serviceTypes: PaginatedServiceTypeList;
  serviceTypeSlug: ServiceTypeSlugEnum;
  initialSearchFilters: LandingSearchFormFilters;
  showSearchFilters?: boolean;
  submitButtonTextVariant?: SubmitButtonTextVariant;
};

const LandingSearchForm: React.FC<Props> = ({
  language,
  serviceTypes,
  serviceTypeSlug,
  initialSearchFilters,
  showSearchFilters = true,
  submitButtonTextVariant = 'search',
}) => {
  const { i18n } = useLingui();
  const { startDate, endDate, setStartDate, setEndDate } = useDates();

  const [searchFilters, setSearchFilters] = useState<LandingSearchFormFilters>({
    ...initialSearchFilters,
  });

  const defaultLocationFilters = useMemo(
    () =>
      pick(initialSearchFilters, [
        'centerlat',
        'centerlng',
        'location',
        'locationAccuracy',
        'locationType',
        'rawLocationTypes',
      ]),
    [initialSearchFilters]
  );

  const onLocationClear = useCallback(() => {
    const newFilters = { ...searchFilters, ...defaultLocationFilters };
    setSearchFilters(newFilters);
  }, [searchFilters, defaultLocationFilters]);

  const onLocationChange = useCallback<React.ComponentProps<typeof Location>['onChange']>(
    (newPartialFilters) => {
      const newFilters = { ...searchFilters };
      Object.entries(newPartialFilters).forEach(([key, value]) => {
        newFilters[key] = value || searchFilters[key];
      });
      setSearchFilters(newFilters);
      emitAnalyticsEvent(new UpdateLocation({}));
    },
    [searchFilters]
  );

  const onServiceTypeChange = useCallback<
    React.ComponentProps<typeof ServiceTypeFilter>['onChange']
  >(
    (newPartialFilters) => {
      const newFilters = { ...searchFilters };
      Object.entries(newPartialFilters).forEach(([key, value]) => {
        newFilters[key] = value || searchFilters[key];
      });
      setSearchFilters(newFilters);
      emitAnalyticsEvent(
        new UpdateServiceType({ selectedServiceType: newPartialFilters.serviceType })
      );
    },
    [searchFilters]
  );

  const searchLink = useMemo(() => {
    return getSearchUrl(
      { ...searchFilters, startDate, endDate },
      language,
      serviceTypes.results?.find((s) => s.slug === searchFilters.serviceType),
      true
    );
  }, [serviceTypes.results, searchFilters, language, startDate, endDate]);

  const serviceTypeOptions = useMemo(
    () =>
      (serviceTypes.results ?? [])
        .filter(
          (s) =>
            s.browsable &&
            (s.suggestedPetType === 'any' ||
              initialSearchFilters.petType.includes(s.suggestedPetType as PetType))
        )
        .map((s) => ({
          title: s.displayName,
          subtitle: s.description,
          value: s.slug as ServiceTypeSlug,
        })),
    [initialSearchFilters, serviceTypes.results]
  );

  return (
    <Flex flexDirection="column">
      {showSearchFilters && (
        <>
          <ServiceTypeFilter
            label={t`Explore other services`}
            filterType={ServiceFilterType.ICON_DROPDOWN}
            onChange={onServiceTypeChange}
            {...{ serviceTypeOptions, searchFilters }}
          />
          <Box my={['4x', '6x']}>
            <Location
              label={i18n._(LOCATION_DEFAULT_TITLE_2)}
              onChange={onLocationChange}
              onClear={onLocationClear}
              locationInputId="landing-search-form-location"
            />
          </Box>
          <FilterLabel>{t`Which dates do you need?`}</FilterLabel>
          <DateRangePicker
            {...{
              startPlaceholder: DATE_PICKER_START_PLACEHOLDER_FOR_SEO_SLUG()[serviceTypeSlug],
              endPlaceholder: DATE_PICKER_END_PLACEHOLDER_FOR_SEO_SLUG()[serviceTypeSlug],
              endDate,
              startDate,
              setStartDate,
              setEndDate,
              serviceType: searchFilters.serviceType,
              language,
              onChange: (dateRange) => {
                dateRange.startDate &&
                  // only fire on the initial startDate change to avoid double tracking
                  !dateRange.endDate &&
                  emitAnalyticsEvent(
                    new SelectDates({
                      serviceTypeSlug,
                    })
                  );
              },
              showArrow: false,
              inline: true,
            }}
          />
        </>
      )}
      <Button
        variant="primary"
        size="large"
        fullWidth
        mt="4x"
        href={searchLink}
        onPress={(): void => {
          emitSearchFormCtaAnalytics({
            serviceTypeSlug,
          });
        }}
      >
        {select(submitButtonTextVariant, {
          search: 'Search',
          dogBoarding: 'Find trusted dog boarders',
          petSitters: 'Find trusted pet sitters',
          houseSitters: 'Find trusted house sitters',
          dayCare: 'Find trusted day care',
          dogWalkers: 'Find trusted dog walkers',
          other: 'Search',
        })}
      </Button>
    </Flex>
  );
};

export default LandingSearchForm;
